import React, { createContext, useContext, useState, useCallback } from 'react';
import { useLocation, Navigate } from 'react-router-dom';

// Meeting mode: the Hub locked to one client while they can see the screen.
//
// Sitting with a client, or sharing a screen with them, every other client's name is one wrong
// click away — the sidebar, the client switcher, the timer bar ("start on Test · Zevi?"), a
// notification. So for the length of the meeting the app shows that client's own pages and
// nothing else: the sidebar and every bar above the page are gone, the client switcher is gone,
// and going anywhere that is not this client sends you back to them. "End meeting" puts it all back.
//
// Kept for the browser tab rather than the account (sessionStorage): a reload in the middle of the
// meeting must not suddenly show everybody, and a second tab opened afterwards is not in the meeting.
// It is about what is on the screen, not about access — the server is unchanged.

const KEY = 'hub_meeting';
const read = () => { try { return JSON.parse(sessionStorage.getItem(KEY)) || null; } catch { return null; } };

const MeetingContext = createContext({ meeting: null, start: () => {}, end: () => {} });
export const useMeeting = () => useContext(MeetingContext);

export function MeetingProvider({ children }) {
  const [meeting, setMeeting] = useState(read);
  const start = useCallback((client) => {
    const m = { id: Number(client.id), name: client.name };
    try { sessionStorage.setItem(KEY, JSON.stringify(m)); } catch { /* private window: still on for this page */ }
    setMeeting(m);
  }, []);
  const end = useCallback(() => {
    try { sessionStorage.removeItem(KEY); } catch { /* nothing kept */ }
    setMeeting(null);
  }, []);
  return <MeetingContext.Provider value={{ meeting, start, end }}>{children}</MeetingContext.Provider>;
}

// Whether a path belongs to the client in the meeting: their page, any tab of it.
export const isMeetingPath = (pathname, id) => pathname === `/clients/${id}` || pathname.startsWith(`/clients/${id}/`);

// Anywhere else — a link out to the Work board, the back button, a typed address, the client
// switcher — goes back to the client. The page asked for is never drawn, not even for a moment:
// an effect that redirected after the render showed the other client's name first, and that
// page's own effects could then send the address back to it.
export function MeetingGuard({ children }) {
  const { meeting } = useMeeting();
  const { pathname } = useLocation();
  if (meeting && !isMeetingPath(pathname, meeting.id)) return <Navigate to={`/clients/${meeting.id}`} replace />;
  return children;
}

export function MeetingBar() {
  const { meeting, end } = useMeeting();
  if (!meeting) return null;
  return (
    <div className="meeting-bar" role="status">
      <span className="meeting-dot" aria-hidden="true" />
      <span>Meeting with <strong>{meeting.name}</strong> — other clients are hidden</span>
      <button className="btn btn-sm meeting-end" onClick={end}>End meeting</button>
    </div>
  );
}
